import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, TOKEN_KEY, extractError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Loads /auth/permissions and stores the effective nav-permission key
  // set for the current user. Called immediately after every path that
  // establishes a user (login, /me on token restore, /bootstrap). See
  // `backend/core/permissions.py` for the key catalog.
  const loadPermissions = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/permissions");
      setPermissions(data?.permissions || []);
    } catch {
      setPermissions([]);
    }
  }, []);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    // Fast path — localStorage already has a token, just call /me.
    if (token) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        await loadPermissions();
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
        setPermissions([]);
      } finally {
        setLoading(false);
      }
      return;
    }
    // Phase 13d — cookie bootstrap. If the user is already logged in on this
    // origin (e.g., they authenticated in the parent Toll iframe context),
    // the HttpOnly session_bootstrap cookie will let us pick up their JWT
    // without a login prompt. `credentials: include` is required so the
    // browser actually attaches the cookie on this cross-context load.
    try {
      const { data } = await api.get("/auth/bootstrap", { withCredentials: true });
      if (data?.access_token) {
        localStorage.setItem(TOKEN_KEY, data.access_token);
        // Immediately load /me so we get counters + full profile shape.
        try {
          const me = await api.get("/auth/me");
          setUser(me.data);
        } catch {
          setUser(data.user);
        }
        await loadPermissions();
      } else {
        setUser(null);
        setPermissions([]);
      }
    } catch {
      // 401 or network error → treat as unauthenticated, same as before.
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  // Poll counters every 60s while a user is authenticated
  useEffect(() => {
    if (!user) return undefined;
    const id = setInterval(() => {
      api.get("/auth/me").then(({ data }) => {
        setUser((u) => (u ? { ...u, counters: data.counters } : data));
      }).catch(() => {});
    }, 60000);
    return () => clearInterval(id);
  }, [user]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      // Fetch /me to include counters block; falls back to login user shape on failure.
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
      } catch {
        setUser(data.user);
      }
      await loadPermissions();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: extractError(e, "Login failed") };
    }
  };

  const refreshCounters = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser((u) => (u ? { ...u, counters: data.counters } : data));
    } catch {
      // ignore
    }
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setPermissions([]);
    // Fire-and-forget clear the session_bootstrap cookie so a subsequent
    // /auth/bootstrap doesn't silently re-log the user in. Cookies aren't
    // cleared by removeItem alone (they're on the backend host).
    api.post("/auth/logout", null, { withCredentials: true }).catch(() => {});
  };

  const hasPerm = useCallback(
    (key) => permissions.includes(key),
    [permissions],
  );

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, reload: loadMe, refreshCounters, permissions, hasPerm, loadPermissions }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
